/**
 * Global QueryClient — Standardized Configuration
 *
 * Single QueryClient instance used across the entire application.
 * Exported here so it can be used for programmatic invalidation
 * outside of React components (e.g., Supabase realtime handlers).
 *
 * Design decisions:
 *   staleTime: 2 min   — data is fresh for 2 minutes after fetch
 *   gcTime:    10 min  — unused query data held in memory for 10 min
 *   retry: 2           — network failures retry twice with exponential backoff
 *   mutations.retry: 0 — mutations never auto-retry (prevent double-submit)
 *   throwOnError: false — errors go to error state, not thrown to boundary
 */
import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:          1000 * 60 * 2,    // 2 minutes
      gcTime:             1000 * 60 * 10,   // 10 minutes
      retry:              2,
      retryDelay:         (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      refetchOnWindowFocus: true,
      refetchOnReconnect:   true,
      throwOnError:       false,
    },
    mutations: {
      retry:        0,        // Never auto-retry mutations
      throwOnError: false,
    },
  },
})

// Expose globally for realtime handlers that run outside React
// Usage: import { queryClient } from '@/lib/queryClient'
//        queryClient.invalidateQueries({ queryKey: qk.cases.all() })
export default queryClient
